// The single normalization boundary for Storyblok-driven forms, promoted from the
// evaluation recorded in 08a. A complete definition is
// normalized once, before the TanStack form is created, and is immutable for one renderer
// mount — a Visual Editor schema change must remount the renderer with a new key.
//
// Every unsupported, unsafe or colliding CMS configuration fails here, loudly, instead of
// silently overwriting values downstream. Adding a field kind means one normalization
// branch below and one renderer branch in components/storyblok-form.

import type { KInputType } from '@vemedia/karibu'
import type { RequestFieldFormat, RequestSubmission } from '@vemedia/request-contract'
import { validEmail } from './form-validation'

export interface StoryblokFormField {
    _uid: string
    component: string
    name: string
    label: string
    placeholder?: string
    required?: boolean
    type?: string
    options?: string
}

export interface StoryblokFormDefinition {
    title: string
    success_message: string
    fields: StoryblokFormField[]
}

interface NormalizedFieldBase {
    uid: string
    name: string
    label: string
    placeholder: string
    required: boolean
}

export interface NormalizedInputField extends NormalizedFieldBase {
    component: 'input-field'
    type: KInputType
}

export interface NormalizedTextareaField extends NormalizedFieldBase {
    component: 'textarea-field'
}

export interface NormalizedSelectField extends NormalizedFieldBase {
    component: 'select-field'
    options: string[]
}

// A Storyblok `input-field` with `type: 'date'` maps to karibu's KDatePicker rather than a
// KInput — the native `<input type="date">` is unstyleable and cannot be suppressed. The
// field value stays an ISO `YYYY-MM-DD` string end to end, exactly what the previous native
// date input submitted.
export interface NormalizedDateField extends NormalizedFieldBase {
    component: 'date-field'
}

export type NormalizedFormField
    = | NormalizedInputField
        | NormalizedTextareaField
        | NormalizedSelectField
        | NormalizedDateField

// Runtime CMS names rule out compile-time field inference; this is the accepted value
// type for the dynamic field set.
export type DynamicFormValues = Record<string, string>

const INPUT_TYPES = new Set<KInputType>([
    'email',
    'number',
    'password',
    'search',
    'tel',
    'text',
    'url',
])
// TanStack Form reads '.' and '[]' in a field name as nested-path syntax, so those are the
// only characters that are genuinely unsafe as a flat form-state key. CMS editors type human
// labels like "Travel Date" or "Number of People" as field names — spaces, capitals and other
// punctuation are legal HTML `name` values and safe object keys, and the previous DOM-based
// renderer accepted them. Don't force identifier-style names; prototype keys are still rejected
// separately below.
const UNSAFE_NAME = /[.[\]]/

export function normalizeFormFields(fields: StoryblokFormField[]): NormalizedFormField[] {
    const names = new Set<string>()
    const uids = new Set<string>()

    return fields.map((field) => {
        const name = field.name.trim()
        const uid = field._uid.trim()

        if (!name || UNSAFE_NAME.test(name)) {
            throw new Error(`Invalid Storyblok form field name: "${field.name}"`)
        }
        // TanStack stores field registrations in a plain object, so prototype keys are
        // unsafe even though they contain no dot/bracket path syntax.
        if (name in Object.prototype) {
            throw new Error(`Unsafe Storyblok form field name: "${name}"`)
        }
        if (names.has(name)) {
            throw new Error(`Duplicate Storyblok form field name: "${name}"`)
        }
        if (!uid || uids.has(uid)) {
            throw new Error(`Invalid or duplicate Storyblok form field uid: "${field._uid}"`)
        }

        names.add(name)
        uids.add(uid)

        const base = {
            uid,
            name,
            label: field.label.trim() || name,
            placeholder: field.placeholder?.trim() ?? '',
            required: Boolean(field.required),
        }

        switch (field.component) {
            case 'input-field': {
                const type = field.type || 'text'

                // Dates render through KDatePicker, not KInput, so they are their own
                // normalized kind rather than a KInputType.
                if (type === 'date') {
                    return { ...base, component: 'date-field' }
                }

                if (!INPUT_TYPES.has(type as KInputType)) {
                    throw new Error(`Unsupported Storyblok input type: "${type}"`)
                }

                return { ...base, component: field.component, type: type as KInputType }
            }
            case 'textarea-field':
                return { ...base, component: field.component }
            case 'select-field': {
                const options = [...new Set(
                    (field.options ?? '').split('\n').map(option => option.trim()).filter(Boolean),
                )]

                if (options.length === 0) {
                    throw new Error(`Storyblok select field has no options: "${name}"`)
                }

                return { ...base, component: field.component, options }
            }
            default:
                throw new Error(`Unsupported Storyblok form field: "${field.component}"`)
        }
    })
}

export function createDynamicDefaults(fields: NormalizedFormField[]): DynamicFormValues {
    return Object.fromEntries(fields.map(field => [field.name, '']))
}

export function validateDynamicField(field: NormalizedFormField, value: string) {
    if (field.required && !value.trim()) {
        return `${field.label} is required.`
    }

    if (field.component === 'input-field' && field.type === 'email' && value) {
        return validEmail(value)
    }
}

function fieldFormat(field: NormalizedFormField): RequestFieldFormat {
    if (field.component === 'date-field')
        return 'date'
    if (field.component === 'textarea-field')
        return 'multiline'
    if (field.component === 'input-field' && field.type === 'email')
        return 'email'
    if (field.component === 'input-field' && field.type === 'tel')
        return 'phone'
    if (field.component === 'input-field' && field.type === 'number')
        return 'number'
    return 'text'
}

export function buildDynamicFormRequest(
    values: DynamicFormValues,
    fields: NormalizedFormField[],
    title: string,
    formId: string,
    pageUrl: string,
    website = '',
): RequestSubmission {
    const email = fields.find(field => field.component === 'input-field' && field.type === 'email')
    const phone = fields.find(field => field.component === 'input-field' && field.type === 'tel')
    const name = fields.find(field => ['fullname', 'full_name', 'name'].includes(field.name.toLowerCase()))
    return {
        kind: 'form',
        title,
        contact: {
            name: name ? values[name.name] || 'Website visitor' : 'Website visitor',
            ...(email && values[email.name] && { email: values[email.name] }),
            ...(phone && values[phone.name] && { phone: values[phone.name] }),
        },
        source: { pageUrl, formId },
        fields: fields.map(field => ({
            key: field.name,
            label: field.label,
            value: values[field.name] ?? '',
            format: fieldFormat(field),
        })),
        website,
    }
}
